import { defaultUniversalAuctionDetail, UniversalAuctionDetail } from 'models/universalData'
import type { AuctionDTO, VehicleListItem } from 'models'

const toNullableString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null

const toStringOrEmpty = (value: unknown): string => (typeof value === 'string' ? value : '')

const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  return null
}

const toNonEmptyString = (value: unknown): string | null => {
  const parsed = toNullableString(value)
  return parsed && parsed.length > 0 ? parsed : null
}

const toAuctionStartDateTime = (value: unknown): AuctionDTO['auctionStartDateTime'] => {
  const raw = toNonEmptyString(value)
  if (!raw) {
    return null
  }

  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return parsed.toISOString() as AuctionDTO['auctionStartDateTime']
}

const extractAuctionVehicleListViewModel = (
  vehicles: Array<VehicleListItem>,
): Array<UniversalAuctionDetail> => {
  return vehicles
    .map((vehicle) => {
      const { vehiclePartial } = vehicle
      const { vehicleDetail } = vehicle
      const { information } = vehicle
      const { pricing } = vehicle

      const viewModel: UniversalAuctionDetail = {
        ...defaultUniversalAuctionDetail,
        auctionStartDateTime: toAuctionStartDateTime(information?.nextAuctionStartTime),
        no: toStringOrEmpty(vehicle.no),
        entityId: toStringOrEmpty(vehicle.entityId),
        passingOrderNo: toNullableNumber(information?.passingOrderNo) ?? 0,
        licensePlate: toStringOrEmpty(vehiclePartial?.licensePlate),
        color: toStringOrEmpty(vehiclePartial?.color),
        type: toNonEmptyString(vehiclePartial?.typeRegDoc),
        dateFirstReg: toStringOrEmpty(
          vehiclePartial?.dateOfFirstRegistration ?? vehiclePartial?.dateFirstReg,
        ),
        id: toNonEmptyString(vehicleDetail?.id ?? vehicle.id),
        version: toNonEmptyString(vehicleDetail?.version ?? vehiclePartial?.engineTrade),
        estimationApporteur: toNullableNumber(pricing?.estimationApporteur),
        coteArgusPriceDate: toNullableNumber(pricing?.coteArgusPriceDate),
        coteArgusKm: toNullableNumber(pricing?.coteArgusKm),
        reserveTTCHF: toNullableNumber(pricing?.reserveTTCHF),
        mileage: toNullableNumber(information?.mileage),
        observationsVendeur: toNullableString(information?.observationsVendeur),
        tvaRecuperable: pricing?.tvaRecuperable ?? null,
        withdrawalPrice: toNullableNumber(pricing?.withdrawalPrice),
        nbPassage: toNullableNumber(information?.nbPassage),
        bidPrice: toNullableNumber(pricing?.bidPrice),
        referenceVendeur: toNullableString(vehicle.vendorInformation?.referenceVendeur),
      }

      return viewModel
    })
    .sort((left, right) => left.passingOrderNo - right.passingOrderNo)
}

export { extractAuctionVehicleListViewModel }
